import { KeyConstants } from '../constants'
import momentTz from 'moment-timezone'

// Utilities
import Preference from './preferenceUtil'
import Logger from './loggerUtil'
import isEmpty from './isEmpty'

class GpsUtil {
  /**
   * Gets the current GPS location of the device, if allowed to.
   * @returns {Promise<{latitude: Number, longitude: Number}>} The current longitude and latitude of the device.
   */
  static async GetCurrentPosition () {
    return new Promise((resolve, reject) => {
      const onGetPositionSuccess = (position) => {
        const finalPosition = GpsUtil.getPositionObject(position)

        resolve({
          latitude: finalPosition.coords.latitude,
          longitude: finalPosition.coords.longitude
        })
      }

      const onGetPositionError = (error) => {
        Logger.error(`Error getting GPS\ncode: ${error.code}\nmessage: ${error.message}`)
        reject(error)
      }

      navigator.geolocation.getCurrentPosition(onGetPositionSuccess, onGetPositionError, { timeout: 10000, maximumAge: 3600000 })

      Preference.set(KeyConstants.gpsPermissionRequested, 'true') // Save into browser so we know its been request as we cannot check existing permissions
    })
  }

  /**
   * Recreated the position object using normal object properties. This is required on Android because the 'position' object uses prototype getters instead of normal props which some methods ignore.
   * @param {Object} position - The original position object with prototype getters as properties.
   * @returns {Object} Recreated position object.
   */
  static getPositionObject (position) {
    const finalPosition = {}

    if ('coords' in position) {
      finalPosition.coords = {}

      if ('latitude' in position.coords) {
        finalPosition.coords.latitude = position.coords.latitude
      }

      if ('longitude' in position.coords) {
        finalPosition.coords.longitude = position.coords.longitude
      }

      if ('accuracy' in position.coords) {
        finalPosition.coords.accuracy = position.coords.accuracy
      }

      if ('altitude' in position.coords) {
        finalPosition.coords.altitude = position.coords.altitude
      }

      if ('altitudeAccuracy' in position.coords) {
        finalPosition.coords.altitudeAccuracy = position.coords.altitudeAccuracy
      }

      if ('heading' in position.coords) {
        finalPosition.coords.heading = position.coords.heading
      }

      if ('speed' in position.coords) {
        finalPosition.coords.speed = position.coords.speed
      }
    }

    if ('timestamp' in position) {
      finalPosition.timestamp = position.timestamp
    }

    return finalPosition
  }

  /**
   * Returns the timezone of the device if available - defaults to America/Chicago
   * @returns {String} Timezone of the device or undefined if no timezone found
   */
  static getTimezone () {
    let timezone

    if (Intl && Intl.DateTimeFormat) {
      const dateTimeFormat = Intl.DateTimeFormat()
      if (dateTimeFormat && dateTimeFormat.resolvedOptions && dateTimeFormat.resolvedOptions().timeZone) {
        timezone = dateTimeFormat.resolvedOptions().timeZone
        Logger.info(`Intl user timezone: ${timezone}`)
        return timezone
      }
    }

    try {
      timezone = momentTz.tz.guess()
    } catch (e) {
      Logger.error('Failed to get timezone')
      Logger.debug(e)
    }

    if (isEmpty(timezone)) {
      Logger.warn('Unable to guess timezone, defaulting')
      return 'America/Chicago'
    }

    Logger.info(`Guessed user timezone: ${timezone}`)
    return timezone
  }
}

export default GpsUtil
